import { MemberTypes, STUDENT_STATUS } from 'src/config/constants'
import type { IFamily, IMember } from 'src/interfaces/family'
import type { IImmunizationRecordPreference } from 'src/interfaces/custom'
import { useAppConfigurationStore } from 'src/stores/appConfiguration.store'
import {
  familyRequiredEmergencyContactCountPath,
  familyMemberRequiredContactFieldsPath,
  getFamilyMemberContactRequirementType,
  normalizeFamilyRequiredEmergencyContactCount,
  normalizeFamilyMemberContactRequirements,
  type FamilyMemberContactRequirementField,
  type FamilyMemberContactRequirements,
} from 'src/utils/memberContactRequirements'
import {
  vaccinationTrackingConfigurationPath,
} from 'src/utils/vaccinationTracking'
import {
  getConfiguredImmunizationRecords,
  mergeSetupImmunizationRecords,
} from 'src/utils/immunizationRecords'
import { getImmunizationRecordStatus } from 'src/utils/immunizationStatus'

export interface OnboardingConditionContext {
  family: IFamily
  loggedInMember?: IMember
  immunizationRecordPreferences?: Array<
    string | IImmunizationRecordPreference
  >
}

export interface OnboardingStep {
  key: string
  title: string
  description?: string
  optional?: boolean
  componentLoader: () => Promise<any>
}

export interface OnboardingStepConfig extends Omit<OnboardingStep, 'optional'> {
  optional?: boolean | ((ctx: OnboardingConditionContext) => boolean)
  memberScope: 'primary' | 'non-primary' | 'both'
  condition: ((ctx: OnboardingConditionContext) => boolean) | null
}

export interface StudentLicensingInfoRequirements {
  requireHealthCareNumber: boolean
  requirePhinNumber: boolean
}

export const requireStudentHealthCareNumberPath =
  'features.requireStudentHealthCareNumber'
export const requireStudentPhinNumberPath = 'features.requireStudentPhinNumber'

const hasActiveStudents = (family: IFamily): boolean =>
  family.students.some(
    (s) =>
      s.status === STUDENT_STATUS.current ||
      s.status === STUDENT_STATUS.pending ||
      s.status === STUDENT_STATUS.future,
  )

const getConfiguredFamilyMemberContactRequirements =
  (): FamilyMemberContactRequirements => {
    const appConfigurationStore = useAppConfigurationStore()
    return normalizeFamilyMemberContactRequirements(
      appConfigurationStore.get(familyMemberRequiredContactFieldsPath),
    )
  }

const getConfiguredRequiredEmergencyContactCount = (): number => {
  const appConfigurationStore = useAppConfigurationStore()
  return normalizeFamilyRequiredEmergencyContactCount(
    appConfigurationStore.get(familyRequiredEmergencyContactCountPath),
  )
}

const getMemberContactRequirementType = (
  member: IMember,
): ReturnType<typeof getFamilyMemberContactRequirementType> => {
  return getFamilyMemberContactRequirementType(
    member.authorizationInfo?.memberType ??
      (member.isPrimaryMember ? MemberTypes.legalGuardian : undefined),
  )
}

const hasValue = (value?: string | null): boolean =>
  typeof value === 'string' && value.trim().length > 0

const isFieldMissing = (
  member: IMember,
  field: FamilyMemberContactRequirementField,
): boolean => {
  switch (field) {
    case 'personalAddress':
      return !(
        hasValue(member.personalContactInfo?.address) &&
        hasValue(member.personalContactInfo?.city) &&
        hasValue(member.personalContactInfo?.state) &&
        hasValue(member.personalContactInfo?.country)
      )
    case 'workAddress':
      return !(
        hasValue(member.workContactInfo?.address) &&
        hasValue(member.workContactInfo?.city) &&
        hasValue(member.workContactInfo?.state) &&
        hasValue(member.workContactInfo?.country)
      )
    case 'personalPhone':
      return !hasValue(member.personalContactInfo?.phoneNumber)
    case 'workPhone':
      return !hasValue(member.workContactInfo?.phoneNumber)
    case 'personalEmail':
      return !hasValue(member.personalContactInfo?.emailAddress)
    case 'workEmail':
      return !hasValue(member.workContactInfo?.emailAddress)
  }
}

export const isMemberMissingConfiguredContactInfo = (
  member: IMember,
  requirements = getConfiguredFamilyMemberContactRequirements(),
): boolean => {
  const requirementType = getMemberContactRequirementType(member)
  if (!requirementType) return false

  return requirements[requirementType].some((field) =>
    isFieldMissing(member, field),
  )
}

export const countValidNonGuardianEmergencyContacts = (
  family: IFamily,
  requirements = getConfiguredFamilyMemberContactRequirements(),
): number =>
  family.members.filter(
    (member) =>
      member.authorizationInfo?.memberType ===
        MemberTypes.nonGuardianEmergencyContact &&
      !member.softDelete &&
      (member.isPortalMember ||
        !isMemberMissingConfiguredContactInfo(member, requirements)),
  ).length

const hasRequiredAgreementTag = (family: IFamily): boolean =>
  family.students.some((s) =>
    s.tags.some(
      (t) => t.name === 'Agreements' && t.requirementLevel === 'Required',
    ),
  )

const isActiveStudentStatus = (status?: string): boolean =>
  status !== STUDENT_STATUS.past && status !== STUDENT_STATUS.inactive

export const getConfiguredStudentLicensingInfoRequirements =
  (): StudentLicensingInfoRequirements => {
    const appConfigurationStore = useAppConfigurationStore()
    return {
      requireHealthCareNumber: appConfigurationStore.get<boolean>(
        requireStudentHealthCareNumberPath,
        false,
      ),
      requirePhinNumber: appConfigurationStore.get<boolean>(
        requireStudentPhinNumberPath,
        false,
      ),
    }
  }

const hasRequiredStudentLicensingInfo = (
  requirements: StudentLicensingInfoRequirements,
): boolean =>
  requirements.requireHealthCareNumber || requirements.requirePhinNumber

export const isStudentMissingRequiredLicensingInfo = (
  student: IFamily['students'][number],
  requirements = getConfiguredStudentLicensingInfoRequirements(),
): boolean => {
  if (!hasRequiredStudentLicensingInfo(requirements)) return false
  if (!isActiveStudentStatus(student.status)) return false

  return (
    (requirements.requireHealthCareNumber &&
      !hasValue(student.healthCareNumber)) ||
    (requirements.requirePhinNumber && !hasValue(student.phinNumber))
  )
}

export const isFamilyMissingRequiredStudentLicensingInfo = (
  family: IFamily,
): boolean =>
  family.students.some((s) => isStudentMissingRequiredLicensingInfo(s))

const doesStudentRequireImmunizationUpdate = (
  student: IFamily['students'][number],
  immunizationRecordPreferences?: Array<
    string | IImmunizationRecordPreference
  >,
): boolean => {
  if (!isActiveStudentStatus(student.status)) return false

  const enableVaccinationTracking = useAppConfigurationStore().get<boolean>(
    vaccinationTrackingConfigurationPath,
    false,
  )

  if (enableVaccinationTracking) {
    return (
      getRequiredImmunizationRecordNames(
        student,
        immunizationRecordPreferences,
      ).length > 0
    )
  }

  return !student.immunizationInfo?.isImmunizationsUpToDate
}

export const getRequiredImmunizationRecordNames = (
  student: IFamily['students'][number],
  immunizationRecordPreferences?: Array<
    string | IImmunizationRecordPreference
  >,
): string[] => {
  if (!isActiveStudentStatus(student.status)) return []

  const mergedInfo = mergeSetupImmunizationRecords(
    student.immunizationInfo,
    immunizationRecordPreferences,
    student.dateOfBirth,
  )

  return getConfiguredImmunizationRecords(
    mergedInfo.immunizationRecord,
    immunizationRecordPreferences,
  )
    .filter((record) =>
      ['Overdue', 'Missing Info'].includes(
        getImmunizationRecordStatus(record),
      ),
    )
    .map((record) => record.name)
}

const doesFamilyRequireImmunizationUpdate = (
  family: IFamily,
  immunizationRecordPreferences?: Array<
    string | IImmunizationRecordPreference
  >,
): boolean =>
  family.students.some((student) =>
    doesStudentRequireImmunizationUpdate(
      student,
      immunizationRecordPreferences,
    ),
  )

const doesFamilyRequireTrackedImmunizationUpdate = (
  family: IFamily,
  immunizationRecordPreferences?: Array<
    string | IImmunizationRecordPreference
  >,
): boolean =>
  useAppConfigurationStore().get<boolean>(
    vaccinationTrackingConfigurationPath,
    false,
  ) &&
  doesFamilyRequireImmunizationUpdate(
    family,
    immunizationRecordPreferences,
  )

const isStepOptional = (
  step: OnboardingStepConfig,
  ctx: OnboardingConditionContext,
): boolean =>
  typeof step.optional === 'function' ? step.optional(ctx) : !!step.optional

export const WELCOME_STEP: OnboardingStep = {
  key: 'welcome',
  title: 'Welcome',
  description: "Welcome to the ChildFriendly portal. Let's get you set up!",
  componentLoader: () =>
    import('src/client/components/onboarding/WelcomeStep.vue'),
}

export const SUBMIT_STEP: OnboardingStep = {
  key: 'submit',
  title: 'Completed Steps',
  description: 'Complete your registration.',
  componentLoader: () =>
    import('src/client/components/onboarding/SubmissionStep.vue'),
}

// All conditional onboarding steps in display order.
// To add a new step: add an entry here with its condition and memberScope.
// - condition determines both routing guard (isOnboarding) and step resolution
// - optional steps do NOT trigger the routing guard on their own
// - condition: null means the step requires async resolution (handled in the step resolver)
export const ONBOARDING_STEPS: OnboardingStepConfig[] = [
  {
    key: 'contactInfo',
    title: 'Contact Information',
    description: 'Complete your contact information to continue.',
    memberScope: 'both',
    condition: (ctx) => {
      if (!ctx.loggedInMember) return false
      if (!hasActiveStudents(ctx.family)) return false
      return isMemberMissingConfiguredContactInfo(ctx.loggedInMember)
    },
    componentLoader: () =>
      import('src/client/components/onboarding/ContactInfoStep.vue'),
  },
  {
    key: 'emergencyContacts',
    title: 'Emergency Contacts',
    description: 'Add emergency contact information for safety.',
    memberScope: 'primary',
    condition: (ctx) => {
      if (!hasActiveStudents(ctx.family)) return false
      return (
        countValidNonGuardianEmergencyContacts(ctx.family) <
        getConfiguredRequiredEmergencyContactCount()
      )
    },
    componentLoader: () =>
      import('src/client/components/onboarding/EmergencyContactStep.vue'),
  },
  {
    key: 'studentInfo',
    title: 'Student Info',
    description: 'Provide additional information about your students.',
    optional: (ctx) =>
      !isFamilyMissingRequiredStudentLicensingInfo(ctx.family) &&
      !doesFamilyRequireTrackedImmunizationUpdate(
        ctx.family,
        ctx.immunizationRecordPreferences,
      ),
    memberScope: 'primary',
    condition: (ctx) =>
      ctx.family.students.some(
        (s) =>
          isStudentMissingRequiredLicensingInfo(s) ||
          doesStudentRequireImmunizationUpdate(
            s,
            ctx.immunizationRecordPreferences,
          ),
      ),
    componentLoader: () =>
      import('src/client/components/onboarding/StudentInfoStep.vue'),
  },
  {
    key: 'customForms',
    title: 'Custom Forms',
    description: 'Complete additional required forms.',
    memberScope: 'primary',
    condition: null,
    componentLoader: () =>
      import('src/client/components/onboarding/OnboardingCustomFormStep.vue'),
  },
  {
    key: 'agreements',
    title: 'Agreements',
    description: 'Review and sign required agreements.',
    memberScope: 'both',
    condition: (ctx) => {
      if (!ctx.loggedInMember) return false
      if (!hasActiveStudents(ctx.family)) return false
      const isAuthorizedSponsor =
        ctx.loggedInMember.isAuthorizedSponsor ?? false
      const needsPAD =
        isAuthorizedSponsor && !ctx.loggedInMember.hasSignedPADAgreement
      if (ctx.loggedInMember.isPrimaryMember)
        return needsPAD || hasRequiredAgreementTag(ctx.family)
      return needsPAD
    },
    componentLoader: () =>
      import('src/client/components/onboarding/AgreementsStep.vue'),
  },
  {
    key: 'paymentInfo',
    title: 'Payment Info',
    description: 'Set up your payment method.',
    optional: true,
    memberScope: 'both',
    condition: (ctx) => {
      if (ctx.loggedInMember?.isPrimaryMember) return true
      return ctx.loggedInMember?.isAuthorizedSponsor ?? false
    },
    componentLoader: () =>
      import('src/client/components/onboarding/PaymentInfoStep.vue'),
  },
]

export const requiresOnboarding = (
  ctx: OnboardingConditionContext,
): boolean => {
  if (ctx.family.students.length === 0) return false
  const scope = ctx.loggedInMember?.isPrimaryMember ? 'primary' : 'non-primary'
  return ONBOARDING_STEPS.some(
    (step) =>
      step.condition !== null &&
      (step.memberScope === 'both' || step.memberScope === scope) &&
      !isStepOptional(step, ctx) &&
      step.condition(ctx),
  )
}

export const requiresPendingAuthorization = (
  ctx: OnboardingConditionContext,
): boolean => {
  if (!ctx.loggedInMember) return false
  if (ctx.loggedInMember.isPrimaryMember) return false
  return hasRequiredAgreementTag(ctx.family)
}
